import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, MapLocKeys } from '@cm/core/localization';

const sharedProps: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	portal: 'SP',
	feature: HubFeature.OtherRequirement,
};

const pathStart = 'Catalogs|Other Requirements|Advanced|Details Form|';

export type SPOtherRequirementsDetailsFormLocNames =
	| 'SPOtherRequirementsDetailsFormLabelName'
	| 'SPOtherRequirementsDetailsFormLabelGroup'
	| 'SPOtherRequirementsDetailsFormLabelStartDate'
	| 'SPOtherRequirementsDetailsFormLabelEndDate'
	| 'SPOtherRequirementsDetailsFormLabelPoints'
	| 'SPOtherRequirementsDetailsFormLabelMaxPoints'
	| 'SPOtherRequirementsDetailsFormLabelValidPeriod'
	| 'SPOtherRequirementsDetailsFormLabelCreateAge'
	| 'SPOtherRequirementsDetailsFormLabelFactor'
	| 'SPOtherRequirementsDetailsFormButtonClearEndDate'
	| 'SPOtherRequirementsDetailsFormFieldErrorGroupRequired'
	| 'SPOtherRequirementsDetailsFormFieldErrorNameRequired'
	| 'SPOtherRequirementsDetailsFormFieldErrorStartDateComparison'
	| 'SPOtherRequirementsDetailsFormFieldErrorStartDateParse'
	| 'SPOtherRequirementsDetailsFormFieldErrorEndDateComparison'
	| 'SPOtherRequirementsDetailsFormFieldErrorEndDateParse'
	| 'SPOtherRequirementsDetailsFormFieldErrorPositiveNumber'
	| 'SPOtherRequirementsDetailsFormFieldErrorPointsExceedMax'
	| 'SPOtherRequirementsDetailsFormFieldErrorParentMaxPointsExceeded'
	| 'SPOtherRequirementsDetailsFormFieldErrorEndDateExpiration'
	| 'SPOtherRequirementsDetailsFormFieldNoGroupsAdded'
	| 'SPOtherRequirementsDetailsFormButtonCancel'
	| 'SPOtherRequirementsDetailsFormButtonSave'
	| 'SPOtherRequirementsDetailsFormButtonAddGroup'
	| 'SPOtherRequirementsDetailsFormButtonAddGroupTooltip';

export const spOtherRequirementsDetailsFormLocs: LocDefs<SPOtherRequirementsDetailsFormLocNames> = {
	SPOtherRequirementsDetailsFormLabelName: {
		defaultValue: 'Name',
		description: 'The label for the name field.',
		displayPath: `${pathStart}Label Name`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormLabelGroup: {
		defaultValue: 'Group',
		description: 'The label for the group field.',
		displayPath: `${pathStart}Label Group`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormLabelStartDate: {
		defaultValue: 'Start Date',
		description: 'The label for the start date field.',
		displayPath: `${pathStart}Label Start Date`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormLabelEndDate: {
		defaultValue: 'End Date',
		description: 'The label for the end date field.',
		displayPath: `${pathStart}Label End Date`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormLabelPoints: {
		defaultValue: 'Points',
		description: 'The label for the points field.',
		displayPath: `${pathStart}Label Points`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormLabelMaxPoints: {
		defaultValue: 'Max Points',
		description: 'The label for the max points field.',
		displayPath: `${pathStart}Label Max Points`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormLabelValidPeriod: {
		defaultValue: 'Valid Period (months)',
		description: 'The label for the valid period field.',
		displayPath: `${pathStart}Label Valid Period`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormLabelCreateAge: {
		defaultValue: 'Create Age (months)',
		description: 'The label for the create age field.',
		displayPath: `${pathStart}Label Create Age`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormLabelFactor: {
		defaultValue: 'Factor',
		description: 'The label for the factor field.',
		displayPath: `${pathStart}Label Factor`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormButtonClearEndDate: {
		defaultValue: 'Clear end date',
		description: 'The button text for clearing the end date.',
		displayPath: `${pathStart}Button Clear End Date`,
		displayType: 'Button',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormFieldErrorGroupRequired: {
		defaultValue: 'Group is required',
		description: 'The error message when group is required.',
		displayPath: `${pathStart}Field Error Group Required`,
		displayType: 'Error',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormFieldErrorNameRequired: {
		defaultValue: 'Name is required',
		description: 'The error message when name is required.',
		displayPath: `${pathStart}Field Error Name Required`,
		displayType: 'Error',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormFieldErrorStartDateComparison: {
		defaultValue: 'The start date must come before the end date',
		description: 'The error message when start date is after end date.',
		displayPath: `${pathStart}Field Error Start Date Comparison`,
		displayType: 'Error',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormFieldErrorStartDateParse: {
		defaultValue: 'You must use a valid date',
		description: 'The error message when start date is invalid.',
		displayPath: `${pathStart}Field Error Start Date Parse`,
		displayType: 'Error',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormFieldErrorEndDateComparison: {
		defaultValue: 'The end date must come after the start date',
		description: 'The error message when end date is before start date.',
		displayPath: `${pathStart}Field Error End Date Comparison`,
		displayType: 'Error',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormFieldErrorEndDateParse: {
		defaultValue: 'You must use a valid date',
		description: 'The error message when end date is invalid.',
		displayPath: `${pathStart}Field Error End Date Parse`,
		displayType: 'Error',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormFieldErrorPositiveNumber: {
		defaultValue: 'Must be a positive number',
		description: 'The error message when a number field must be positive.',
		displayPath: `${pathStart}Field Error Positive Number`,
		displayType: 'Error',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormFieldErrorPointsExceedMax: {
		defaultValue: 'Points cannot exceed max points',
		description: 'The error message when points exceed max points.',
		displayPath: `${pathStart}Field Error Points Exceed Max`,
		displayType: 'Error',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormFieldErrorParentMaxPointsExceeded: {
		defaultValue: "The max points you entered is higher than the parent's max points of ___.",
		description: 'The error message when max points exceed parent CE Activity max points.',
		displayPath: `${pathStart}Field Error Parent Max Points Exceeded`,
		displayType: 'Error',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormFieldErrorEndDateExpiration: {
		defaultValue:
			'End date must be in the future, or one day previous to today, for expiration purposes.',
		description: 'The error message when end date is not valid for expiration purposes.',
		displayPath: `${pathStart}Field Error End Date Expiration`,
		displayType: 'Error',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormFieldNoGroupsAdded: {
		defaultValue: 'No groups added',
		description: 'The message shown when no groups are added.',
		displayPath: `${pathStart}Field No Groups Added`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormButtonCancel: {
		defaultValue: 'Cancel',
		description: 'The button text for canceling the form.',
		displayPath: `${pathStart}Button Cancel`,
		displayType: 'Button',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormButtonSave: {
		defaultValue: 'Save',
		description: 'The button text for saving the form.',
		displayPath: `${pathStart}Button Save`,
		displayType: 'Button',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormButtonAddGroup: {
		defaultValue: 'Add Group',
		description: 'The button text for adding a group.',
		displayPath: `${pathStart}Button Add Group`,
		displayType: 'Button',
		...sharedProps,
	},
	SPOtherRequirementsDetailsFormButtonAddGroupTooltip: {
		defaultValue: 'Add new group',
		description: 'The tooltip text for adding a group.',
		displayPath: `${pathStart}Add Group Tooltip`,
		displayType: 'Content',
		...sharedProps,
	},
};

export const LOC_KEYS = MapLocKeys<SPOtherRequirementsDetailsFormLocNames>(
	spOtherRequirementsDetailsFormLocs
);
